import React from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ChevronDown, ChevronUp, Bus, Check, Lock, AlertTriangle, Send, Loader2, MessageSquareText, Info } from 'lucide-react';
import { LocationCell } from './LocationCell';
import { BookingDetailsTable } from './BookingDetailsTable';
import { isUnassignedTrip } from './tripAssignment';
import { PassengerListLinkCell } from './PassengerListLinkCell';
import type { TripLink } from './useDriverPlanPublications';
import { FlightNumberChips, FlightTimeChips } from './FlightCells';
import { tripTypeCode, tripTypeTitle } from './tripType';
import { format, parseISO, getDay, addDays } from 'date-fns';
import { nb, enUS } from 'date-fns/locale';

// Dag-navn og dato oversettes i klienten fra `day.date` (backend sender norsk tekst).
const dayLabel = (day: any, lang: string) => {
  try {
    const d = parseISO(day.date);
    const locale = lang === 'en' ? enUS : nb;
    const name = format(d, 'EEEE', { locale });
    const date = format(d, lang === 'en' ? 'd MMMM' : 'd. MMMM', { locale });
    return { name: name.charAt(0).toUpperCase() + name.slice(1), date, weekend: [0, 6].includes(getDay(d)) };
  } catch {
    return { name: day.day_name, date: day.formatted_date, weekend: ['Lørdag', 'Søndag', 'Saturday', 'Sunday'].includes(day.day_name) };
  }
};

interface WeeklyDateViewProps {
  weeklyPlan: any;
  isStaff: boolean;
  maxStopsInWeek: number;
  selectedDrivers: string[];
  showUnassignedOnly: boolean;
  selectedTrips: Set<string>;
  setSelectedTrips: (s: Set<string>) => void;
  expandedRows: Set<string>;
  setExpandedRows: (s: Set<string>) => void;
  getLocationColor: (loc: string) => string | null;
  getLocationShortName: (loc: string) => string;
  getDriverColor: (name: string) => string | null;
  getDriverDisplayName?: (name: string) => string;
  getCompanyShortName: (name: string) => string;
  handleViewDetails: (tripNumber: string) => void;
  setSeatRequestModal: (val: any) => void;
  onOpenTripDrawer?: (trip: any, date?: string) => void;
  outOfScope?: (trip: any) => boolean;
  /** Iter 797: hurtigsending av passasjerliste-PDF til turens sjåfør (e-post). */
  onSendToDriver?: (trip: any) => void;
  sendingTrips?: Record<string, boolean>;
  /** Kundelenken (/kp/): samme rutenett og kolonner som stab, men uten avkryssing, ekspandering, tildelingshint og Notat. */
  readOnly?: boolean;
  hideNotes?: boolean;
  /** Ekstra innhold i dagoverskriften (f.eks. «Publisert til kunde»-badge + knapper i intern Ukesplan). */
  dayHeaderExtra?: (day: any) => React.ReactNode;
  /** Iter 798: kolonnen «Liste» — turens egen passasjerliste-lenke (/pl/), aktiv når datoen er publisert til sjåfør/eksterne. */
  tripLinks?: Record<string, TripLink>;
  /** Iter 831: ekstra kolonner bakerst (sjåførlenken /sp/: «Liste» og «Merknad») — samme rutenett som Ukesplanen ellers. */
  extraColumns?: ExtraColumn[];
  /** Skjul sjåførmerknad-badgen i Pax-kolonnen (når merknaden vises i egen kolonne). */
  hideDriverNoteBadge?: boolean;
  /** Iter 842: «Bekreftet»-avkryssing bakerst (kun intern Ukesplan). Auto på ved sjåførens portalbekreftelse; kontoret overstyrer. */
  onToggleConfirmed?: (trip: any, confirmed: boolean) => Promise<void> | void;
}

export interface ExtraColumn {
  key: string;
  header: React.ReactNode;
  cell: (trip: any, day: any) => React.ReactNode;
  className?: string;
}

export function WeeklyDateView({
  weeklyPlan, isStaff, maxStopsInWeek,
  selectedDrivers, showUnassignedOnly,
  selectedTrips, setSelectedTrips,
  expandedRows, setExpandedRows,
  getLocationColor, getLocationShortName,
  getDriverColor, getDriverDisplayName, getCompanyShortName,
  handleViewDetails, setSeatRequestModal, onOpenTripDrawer, outOfScope,
  readOnly = false, hideNotes = false, dayHeaderExtra, onSendToDriver, sendingTrips, tripLinks, extraColumns, hideDriverNoteBadge, onToggleConfirmed }: WeeklyDateViewProps) {
  const { t, i18n } = useTranslation();
  const canEdit = isStaff && !readOnly;
  const staffLayout = isStaff || readOnly;
  const showNotes = isStaff && !readOnly && !hideNotes;
  const showLinks = canEdit && !!tripLinks;

  return (
    <>
      {weeklyPlan.days.map((day: any) => {
        let dayTrips = day.trips.filter((trip: any) => {
          if (showUnassignedOnly && !isUnassignedTrip(trip)) return false;
          if (selectedDrivers.length > 0) {
            if (!selectedDrivers.includes(trip.driver_id) && !selectedDrivers.includes(`ext_${trip.external_company}`)) return false;
          }
          return true;
        });

        if (dayTrips.length === 0 && (showUnassignedOnly || selectedDrivers.length > 0)) return null;

        const label = dayLabel(day, i18n.language);
        return (
          <div key={day.date} className="border rounded-lg overflow-hidden print:break-inside-avoid" data-testid={`weekly-day-${day.date}`}>
            <div className={`px-4 py-2 font-bold flex flex-wrap items-center justify-between gap-x-4 gap-y-1 ${
              label.weekend
                ? 'bg-amber-100 text-amber-800'
                : 'bg-blue-100 text-blue-800'
            }`}>
              <span>{label.name} - {label.date}</span>
              {dayHeaderExtra ? <span className="flex-1 min-w-[12rem]">{dayHeaderExtra(day)}</span> : null}
              <span className="text-sm font-normal" data-testid={`weekly-day-count-${day.date}`}>{dayTrips.length} {dayTrips.length === 1 ? t('trips.tripLowercase', 'tur') : t('trips.tripsLowercase', 'turer')}</span>
            </div>

            {dayTrips.length === 0 ? (
              <div className="px-4 py-4 text-muted-foreground text-sm text-center bg-muted/50">
                {t('trips.noTripsThisDay', 'Ingen turer denne dagen')}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-muted/50 border-b">
                    <tr>
                      {canEdit && (
                        <th className="px-1 py-1.5 text-center font-semibold w-8">
                          <Checkbox
                            checked={dayTrips.every((t: any) => selectedTrips.has(t.trip_number))}
                            onCheckedChange={(checked: any) => {
                              const newSelected = new Set(selectedTrips);
                              dayTrips.forEach((t: any) => {
                                if (checked) newSelected.add(t.trip_number);
                                else newSelected.delete(t.trip_number);
                              });
                              setSelectedTrips(newSelected);
                            }}
                            className="w-3 h-3"
                          />
                        </th>
                      )}
                      {staffLayout && <th className="px-2 py-1.5 text-left font-semibold">{t('weeklyDateView.tur', 'Tur')}</th>}
                      <th className="px-2 py-1.5 text-left font-semibold">{t('weeklyDateView.fra', 'Fra')}</th>
                      {Array.from({ length: maxStopsInWeek }, (_: any, i: any) => (
                        <th key={`stop-${i}`} className="px-2 py-1.5 text-left font-semibold">St{i + 1}</th>
                      ))}
                      <th className="px-2 py-1.5 text-left font-semibold">{t('weeklyDateView.til', 'Til')}</th>
                      <th className="px-2 py-1.5 text-left font-semibold">{t('weeklyDateView.avg', 'Avg.')}</th>
                      {Array.from({ length: maxStopsInWeek }, (_: any, i: any) => (
                        <th key={`st-time-${i}`} className="px-2 py-1.5 text-left font-semibold">T{i + 1}</th>
                      ))}
                      {/* Driver/Vehicle/Flight info is now always visible — drivers
                          on "Mine kjøreplaner" need to see who else is driving and
                          which bus is used. Customers also benefit from knowing
                          flight number and remaining seats on shared rides. */}
                      <th className="px-2 py-1.5 text-left font-semibold" data-testid="weekly-col-flight-no">{t('weeklyDateView.flynr', 'Flynr')}</th>
                      <th className="px-2 py-1.5 text-left font-semibold" data-testid="weekly-col-flight-time">{t('weeklyDateView.flytid', 'Flytid')}</th>
                      <th className="px-2 py-1.5 text-center font-semibold">{t('trips.colPax', 'Pax')}</th>
                      <th className="px-2 py-1.5 text-left font-semibold">{t('common.driver', 'Sjåfør')}</th>
                      <th className="px-2 py-1.5 text-left font-semibold">{t('common.vehicle', 'Kjøretøy')}</th>
                      <th className="px-2 py-1.5 text-center font-semibold" title={t('weeklyDateView.typeTitle', 'Type som i Booking-listen: T taxi · MB minibuss · B buss · S = delt · P per passasjer')} data-testid="weekly-col-type">{t('weeklyDateView.type', 'Type')}</th>
                      {canEdit && <th className="px-2 py-1.5 text-left font-semibold" title={t('weeklyDateView.kunderPaxTitle', 'Kunder på turen med antall passasjerer per kunde')} data-testid="weekly-col-customers">{t('weeklyDateView.kunderPax', 'Kunder (pax)')}</th>}
                      {showNotes && <th className="px-2 py-1.5 text-left font-semibold">{t('common.note', 'Notat')}</th>}
                      {showLinks && <th className="px-2 py-1.5 text-left font-semibold" title={t('driverPlanPublish.listeKolonneTitle', 'Passasjerliste-lenke per tur (publiser til sjåfør/eksterne først)')} data-testid="weekly-pl-link-header">{t('driverPlanPublish.listeKolonne', 'Liste')}</th>}
                      {extraColumns?.map((c) => <th key={c.key} className={`px-2 py-1.5 text-left font-semibold ${c.className || ''}`} data-testid={`weekly-col-${c.key}`}>{c.header}</th>)}
                      {canEdit && onToggleConfirmed && <th className="px-2 py-1.5 text-center font-semibold" title={t('weeklyDateView.bekreftetTitle', 'Settes automatisk når sjåføren bekrefter turen i portalen — kontoret kan huke av/overstyre manuelt')} data-testid="weekly-col-confirmed">{t('weeklyDateView.bekreftet', 'Bekreftet')}</th>}
                      {!staffLayout && (
                        <>
                          <th className="px-2 py-1.5 text-center font-semibold">{t('trips.availableSeats', 'Ledige plasser')}</th>
                          <th className="px-2 py-1.5 text-center font-semibold">{t('trips.myBooking', 'Min booking')}</th>
                          <th className="px-2 py-1.5 text-center font-semibold"></th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {dayTrips.map((trip: any, idx: any) => (
                      <TripRow
                        key={trip.trip_number}
                        trip={trip}
                        idx={idx}
                        isStaff={isStaff}
                        maxStopsInWeek={maxStopsInWeek}
                        selectedTrips={selectedTrips}
                        setSelectedTrips={setSelectedTrips}
                        expandedRows={expandedRows}
                        setExpandedRows={setExpandedRows}
                        getLocationColor={getLocationColor}
                        getLocationShortName={getLocationShortName}
                        getDriverColor={getDriverColor}
                        getDriverDisplayName={getDriverDisplayName}
                        getCompanyShortName={getCompanyShortName}
                        handleViewDetails={handleViewDetails}
                        setSeatRequestModal={setSeatRequestModal}
                        onOpenTripDrawer={onOpenTripDrawer}
                        onSendToDriver={onSendToDriver}
                        sendingTrips={sendingTrips}
                        tripLinks={tripLinks}
                        tripDate={day.date}
                        day={day}
                        extraColumns={extraColumns}
                        hideDriverNoteBadge={hideDriverNoteBadge}
                        onToggleConfirmed={onToggleConfirmed}
                        t={t}
                        outOfScope={!!outOfScope?.(trip)}
                        readOnly={readOnly}
                        hideNotes={hideNotes}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}

function TripRow({
  trip, idx, isStaff, maxStopsInWeek,
  selectedTrips, setSelectedTrips,
  expandedRows, setExpandedRows,
  getLocationColor, getLocationShortName,
  getDriverColor, getDriverDisplayName, getCompanyShortName,
  handleViewDetails, setSeatRequestModal,
  onOpenTripDrawer, tripDate, onSendToDriver, sendingTrips, tripLinks, day, extraColumns, hideDriverNoteBadge, onToggleConfirmed,
  t, showDate, dateLabel, outOfScope, readOnly = false, hideNotes = false }: any) {
  const canEdit = isStaff && !readOnly;
  const staffLayout = isStaff || readOnly;
  const showNotes = isStaff && !readOnly && !hideNotes;
  const showLinks = canEdit && !!tripLinks;
  const planStatus = readOnly ? trip.plan_status : null; // innstilt | endret | ny (kun kundelenken)
  const rawDriverName = trip.external_company || trip.driver_name || '';
  // Interne sjåfører: kun fullt fornavn (fornavn + evt. mellomnavn).
  // Eksterne selskaper: uendret.
  const driverName = trip.external_company
    ? rawDriverName
    : (getDriverDisplayName ? getDriverDisplayName(rawDriverName) : rawDriverName);
  const driverStatus = trip.driver_status;
  const driverColor = getDriverColor(rawDriverName);

  const getStatusIcon = () => {
    if (!driverName) return null;
    switch (driverStatus) {
      case 'bekreftet': return <span className="text-green-600 font-bold text-[10px]">v</span>;
      case 'forespurt': return <span className="text-amber-500 font-bold text-[10px]">o</span>;
      case t('auto.avsltt_2', 'rejected'): return <span className="text-red-500 font-bold text-[10px]">x</span>;
      default: return <span className="text-amber-500 font-bold text-[10px]">o</span>;
    }
  };

  return (
    <React.Fragment>
      <tr className={`hover:bg-blue-50 ${idx % 2 === 0 ? 'bg-background' : 'bg-muted/50/50'} ${
        !isStaff && trip.is_my_booking ? 'bg-green-50 hover:bg-green-100' : ''
      } ${selectedTrips.has(trip.trip_number) ? 'bg-blue-100' : ''} ${outOfScope ? 'opacity-60' : ''} ${planStatus === 'innstilt' ? 'opacity-70 line-through decoration-red-400' : ''}`}
      title={outOfScope ? 'Skrivebeskyttet — utenfor ditt rute-/avdelingsansvar' : undefined}
      data-testid={outOfScope ? `weekly-trip-readonly-${trip.trip_number}` : `weekly-trip-row-${trip.trip_number}`}
      data-plan-status={planStatus || undefined}>
        {canEdit && (
          <td className="px-1 py-1.5 text-center">
            <Checkbox
              checked={selectedTrips.has(trip.trip_number)}
              onCheckedChange={(checked: any) => {
                const newSelected = new Set(selectedTrips);
                if (checked) newSelected.add(trip.trip_number);
                else newSelected.delete(trip.trip_number);
                setSelectedTrips(newSelected);
              }}
              className="w-3 h-3"
              data-testid={`weekly-trip-select-${trip.trip_number}`}
            />
          </td>
        )}
        {staffLayout && (
          <td className="px-2 py-1.5">
            <div className="flex items-center gap-1">
              {canEdit && (
                <button
                  onClick={() => {
                    const newExpanded = new Set(expandedRows);
                    if (newExpanded.has(trip.trip_number)) newExpanded.delete(trip.trip_number);
                    else newExpanded.add(trip.trip_number);
                    setExpandedRows(newExpanded);
                  }}
                  className="p-0.5 hover:bg-muted rounded"
                  data-testid={`weekly-trip-expand-${trip.trip_number}`}
                >
                  {expandedRows.has(trip.trip_number) ? (
                    <ChevronUp className="w-3 h-3 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="w-3 h-3 text-muted-foreground" />
                  )}
                </button>
              )}
              {canEdit ? (
                <>
                  {/* Iter 818: turnummer åpner SAMME redigeringsbilde som fra Booking-listen (Tilegn tur-editoren) */}
                  <span
                    className="font-mono text-blue-600 cursor-pointer hover:underline whitespace-nowrap"
                    onClick={() => handleViewDetails(trip.trip_number)}
                    title={t('weeklyDateView.apneRedigering', 'Åpne tur for redigering')}
                    data-testid={`weekly-trip-open-${trip.trip_number}`}
                  >
                    {trip.trip_number}
                  </span>
                  {onOpenTripDrawer && (
                    <button
                      type="button"
                      className="text-slate-400 hover:text-blue-600 transition-colors"
                      onClick={(e) => { e.stopPropagation(); onOpenTripDrawer(trip, tripDate); }}
                      title={t('weeklyDateView.turdetaljer', 'Turdetaljer (lesevisning)')}
                      aria-label={t('weeklyDateView.turdetaljer', 'Turdetaljer (lesevisning)')}
                      data-testid={`weekly-trip-info-${trip.trip_number}`}
                    >
                      <Info className="w-3 h-3" />
                    </button>
                  )}
                </>
              ) : (
                <span className="font-mono text-slate-700 whitespace-nowrap" data-testid={`weekly-trip-number-${trip.trip_number}`}>{trip.trip_number}</span>
              )}
              {planStatus === 'innstilt' && <span className="text-[9px] font-semibold px-1 py-0.5 rounded bg-red-100 text-red-700 border border-red-200 no-underline" data-testid={`weekly-status-${trip.trip_number}`}>{t('weeklyDateView.innstilt', 'Innstilt')}</span>}
              {planStatus === 'endret' && <span className="text-[9px] font-semibold px-1 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-200" data-testid={`weekly-status-${trip.trip_number}`}>{t('weeklyDateView.endret', 'Endret')}</span>}
              {planStatus === 'ny' && <span className="text-[9px] font-semibold px-1 py-0.5 rounded bg-emerald-100 text-emerald-700 border border-emerald-200" data-testid={`weekly-status-${trip.trip_number}`}>{t('weeklyDateView.ny', 'Ny')}</span>}
              {outOfScope && <Lock className="w-3 h-3 text-slate-400" title={t('weeklyDateView.skrivebeskyttetUtenforDittRuteAvdelingsa', 'Skrivebeskyttet — utenfor ditt rute-/avdelingsansvar')} />}
              {trip.multiple_airports && isStaff && (
                // Lesbart flagg: eksisterende tur med mer enn én flyplass i ruten (ny gruppering blokkeres server-side)
                <span className="inline-flex items-center gap-0.5 text-[9px] font-semibold px-1 py-0.5 rounded bg-orange-100 text-orange-800 border border-orange-300 whitespace-nowrap"
                  title={`${t('weeklyDateView.flereFlyplasserTitle', 'Ruten har mer enn én flyplass')}: ${(trip.airports || []).map((a: string) => a.toUpperCase()).join(' + ')}`}
                  data-testid={`weekly-multi-airport-${trip.trip_number}`}>
                  <AlertTriangle className="w-2.5 h-2.5" />{t('weeklyDateView.flereFlyplasser', 'Flere flyplasser')}
                </span>
              )}
              {(trip.stops?.length || 0) > 0 && (
                <span
                  className="text-[9px] font-medium px-1 py-0.5 rounded bg-violet-100 text-violet-700 border border-violet-200 whitespace-nowrap"
                  title={`${trip.stops.length} ${t('weeklyDateView.mellomstopp', 'mellomstopp')}: ${(trip.stops || []).map((s: any) => s.location).filter(Boolean).join(' · ')}`}
                  data-testid={`weekly-via-${trip.trip_number}`}
                >
                  via {trip.stops.length}
                </span>
              )}
            </div>
          </td>
        )}
        {showDate && (
          <td className="px-2 py-1.5 text-[10px] text-muted-foreground">{dateLabel}</td>
        )}
        <td className="px-2 py-1.5">
          <LocationCell location={trip.from_location} getLocationColor={getLocationColor} getShortName={getLocationShortName} />
        </td>
        {Array.from({ length: maxStopsInWeek }, (_: any, i: any) => (
          <td key={`stop-cell-${i}`} className="px-2 py-1.5">
            <LocationCell location={trip.stops?.[i]?.location} getLocationColor={getLocationColor} getShortName={getLocationShortName} />
          </td>
        ))}
        <td className="px-2 py-1.5">
          <LocationCell location={trip.to_location} getLocationColor={getLocationColor} getShortName={getLocationShortName} />
        </td>
        <td className="px-2 py-1.5 font-mono">{trip.departure_time || '—'}</td>
        {Array.from({ length: maxStopsInWeek }, (_: any, i: any) => (
          <td key={`st-time-cell-${i}`} className="px-2 py-1.5 font-mono text-muted-foreground">
            {trip.stop_times?.[i] || '—'}
          </td>
        ))}
        {/* Iter 809: Flynr og Flytid som to kolonner (separate lister). */}
        <td className="px-2 py-1.5" data-testid={`trip-flights-${trip.trip_number}`}><FlightNumberChips trip={trip} t={t} /></td>
        <td className="px-2 py-1.5" data-testid={`trip-flight-times-${trip.trip_number}`}><FlightTimeChips trip={trip} t={t} /></td>
        <td className="px-2 py-1.5 text-center">
          <span className="inline-flex items-center gap-1">
            <Badge variant="secondary" className="text-[10px]">{trip.total_passengers}</Badge>
            {staffLayout && ((trip.checked_in_count || 0) > 0 || (trip.live_checkin && (trip.total_passengers || 0) > 0)) && (
              // Opptalt pax: sjåførens avhukninger i appen, synkes hit automatisk. Live (dagens turer): oppdateres hvert 15. s.
              <Badge
                variant="outline"
                className={`text-[10px] gap-0.5 transition-colors ${(trip.checked_in_count || 0) >= (trip.total_passengers || 0) ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-sky-300 bg-sky-50 text-sky-700'} ${trip.live_changed_at && Date.now() - trip.live_changed_at < 20000 ? 'ring-2 ring-emerald-300 ring-offset-1' : ''}`}
                title={trip.live_checkin ? t('weeklyDateView.ombordNaTittel', 'Ombord nå – live fra sjåførens skanning (oppdateres hvert 15. sekund) / Tot. pax') : t('weeklyDateView.opptaltTittel', 'Opptalt av sjåfør (avhuket i appen) / Tot. pax')}
                data-testid={`weekly-counted-${trip.trip_number}`}
                data-live={trip.live_checkin ? 'true' : undefined}
              >
                {trip.live_checkin ? (
                  <span className="relative flex h-1.5 w-1.5 mr-0.5" aria-hidden="true">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  </span>
                ) : <Check className="w-2.5 h-2.5" />}
                {trip.checked_in_count || 0}/{trip.total_passengers}
              </Badge>
            )}
            {staffLayout && !hideDriverNoteBadge && trip.driver_note?.note && (
              // Iter 802: sjåførens merknad fra kjøreplan-lenken (/sp/) – hele teksten ved hover.
              <Badge
                variant="outline"
                className="text-[10px] gap-0.5 border-amber-300 bg-amber-50 text-amber-800 max-w-[140px] truncate"
                title={`${t('weeklyDateView.sjaforMerknad', 'Sjåførmerknad')} · ${trip.driver_note.by || ''}: ${trip.driver_note.note}`}
                data-testid={`weekly-driver-note-${trip.trip_number}`}
              >
                <MessageSquareText className="w-2.5 h-2.5 shrink-0" /><span className="truncate">{trip.driver_note.note}</span>
              </Badge>
            )}
          </span>
        </td>
        <td
          className={`px-2 py-1.5 ${outOfScope || readOnly ? '' : 'cursor-pointer'}`}
          onDoubleClick={outOfScope || readOnly ? undefined : (e: any) => { e.stopPropagation(); handleViewDetails(trip.trip_number); }}
          title={readOnly ? undefined : outOfScope ? 'Skrivebeskyttet — utenfor ditt rute-/avdelingsansvar' : 'Dobbeltklikk for å tildele sjåfør/ekstern'}
          data-testid={`weekly-driver-cell-${trip.trip_number}`}
        >
          {!driverName ? (
            <span className="text-muted-foreground text-[10px] italic">{readOnly ? '—' : outOfScope ? 'Skrivebeskyttet' : t('weeklyDateView.dobbeltklikkForATildele', 'Dobbeltklikk for å tildele')}</span>
          ) : (
            <span className="inline-flex items-center gap-1">
              <span
                className={`text-[10px] font-medium inline-flex items-center gap-1 whitespace-nowrap ${driverColor ? 'px-1.5 py-0.5 rounded' : ''}`}
                style={driverColor ? { backgroundColor: `${driverColor}30`, borderLeft: `3px solid ${driverColor}`, color: '#1e293b' } : undefined}
              >
                {getStatusIcon()}{driverName}
              </span>
              {canEdit && onSendToDriver && !outOfScope && (
                <button
                  type="button"
                  className="inline-flex items-center justify-center h-5 w-5 rounded text-violet-600 hover:bg-violet-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  title={trip.driver_email
                    ? t('weeklyDateView.sendListeTilSjafor', { defaultValue: 'Send passasjerliste (PDF) til {{email}}', email: trip.driver_email })
                    : t('weeklyDateView.sjaforManglerEpost', 'Sjåføren mangler e-post – bruk «Send til sjåfør» i Passasjerliste-menyen for SMS')}
                  disabled={!trip.driver_email || !!sendingTrips?.[trip.trip_number]}
                  onClick={(e: any) => { e.stopPropagation(); onSendToDriver(trip); }}
                  data-testid={`weekly-send-driver-${trip.trip_number}`}
                >
                  {sendingTrips?.[trip.trip_number] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                </button>
              )}
              {trip.assigned_by_rule ? (
                <span
                  title={t('weeklyDateView.tildeltViaRuteregel', { defaultValue: 'Tildelt via ruteregel: {{from}} → {{to}} → {{assignee}}', from: trip.assigned_by_rule.from_location || '?', to: trip.assigned_by_rule.to_location || '?', assignee: trip.assigned_by_rule.assignee_name || '' })}
                  className="text-[9px] font-medium px-1 py-0.5 rounded bg-violet-100 text-violet-700 border border-violet-200"
                  data-testid={`trip-rule-badge-${trip.trip_number}`}
                >
                  {t('weeklyDateView.regel', 'Regel')}
                </span>
              ) : null}
            </span>
          )}
        </td>
        <td
          className={`px-2 py-1.5 ${outOfScope || readOnly ? '' : 'cursor-pointer'}`}
          onDoubleClick={outOfScope || readOnly ? undefined : (e: any) => { e.stopPropagation(); handleViewDetails(trip.trip_number); }}
          title={readOnly ? undefined : outOfScope ? 'Skrivebeskyttet — utenfor ditt rute-/avdelingsansvar' : t('weeklyDateView.dobbeltklikkForATildeleKjoretoy', 'Dobbeltklikk for å tildele kjøretøy')}
          data-testid={`weekly-vehicle-cell-${trip.trip_number}`}
        >
          {trip.vehicle ? (
            <span className="text-[10px] font-mono inline-flex items-center gap-1 whitespace-nowrap"><Bus className="w-3 h-3" />{trip.vehicle}</span>
          ) : (
            <span className="text-muted-foreground text-[10px] italic">{readOnly ? '—' : outOfScope ? 'Skrivebeskyttet' : t('weeklyDateView.dobbeltklikkForATildele', 'Dobbeltklikk for å tildele')}</span>
          )}
        </td>
        <td className="px-2 py-1.5 text-center" data-testid={`weekly-type-${trip.trip_number}`}><TripTypeCode trip={trip} /></td>
        {canEdit && (
          <td className="px-2 py-1.5 align-top" data-testid={`weekly-customers-${trip.trip_number}`}>
            <CustomerPaxList trip={trip} getCompanyShortName={getCompanyShortName} />
          </td>
        )}
        {showNotes && (
          <td className="px-2 py-1.5 text-muted-foreground truncate max-w-[150px]" title={[trip.driver_note?.note ? `${t('weeklyDateView.sjafor', 'Sjåfør')}: ${trip.driver_note.note}` : '', trip.notes || ''].filter(Boolean).join(' · ')} data-testid={`weekly-notes-${trip.trip_number}`}>
            {trip.driver_note?.note && <span className="text-amber-800 font-medium">{t('weeklyDateView.sjafor', 'Sjåfør')}: {trip.driver_note.note}{trip.notes ? ' · ' : ''}</span>}{trip.notes || ''}
          </td>
        )}
        {showLinks && (
          <td className="px-2 py-1.5"><PassengerListLinkCell tripNumber={trip.trip_number} link={tripLinks?.[trip.trip_number]} /></td>
        )}
        {extraColumns?.map((c: any) => (
          // inline-block stopper rad-nivå line-through (innstilt) fra å treffe lenke/merknadseditor
          <td key={c.key} className={`px-2 py-1.5 ${c.className || ''}`} data-testid={`weekly-${c.key}-${trip.trip_number}`}><span className="inline-block max-w-full">{c.cell(trip, day)}</span></td>
        ))}
        {canEdit && onToggleConfirmed && (
          <td className="px-2 py-1.5 text-center" onClick={(e) => e.stopPropagation()}>
            <TripConfirmedCheckbox trip={trip} onToggle={onToggleConfirmed} tripDate={day?.date || tripDate} />
          </td>
        )}
        {!staffLayout && (
          <>
            <td className="px-2 py-1.5 text-center">
              {trip.free_seats === 0 ? (
                <Badge className="bg-red-100 text-red-700 text-[10px]">{t('weeklyDateView.fullt', 'Fullt')}</Badge>
              ) : trip.free_seats <= 3 ? (
                <Badge className="bg-amber-100 text-amber-700 text-[10px]">{t('weeklyDateView.ja', { defaultValue: 'Ja ({{v1}})', v1: trip.free_seats })}</Badge>
              ) : (
                <Badge className="bg-green-100 text-green-700 text-[10px]">{t('common.yes', 'Ja')}</Badge>
              )}
            </td>
            <td className="px-2 py-1.5 text-center">
              {trip.is_my_booking ? (
                <div className="flex items-center justify-center gap-1">
                  <Check className="w-3 h-3 text-green-600" />
                  <span className="text-green-700 text-[10px] font-medium">{trip.my_passengers} pax</span>
                </div>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </td>
            <td className="px-2 py-1.5 text-center">
              {trip.free_seats > 0 && !trip.is_my_booking && (
                <Button variant="outline" size="sm" className="h-6 text-[10px] px-2" onClick={() => setSeatRequestModal({ open: true, trip })}>
                  {t('weeklyDateView.beOmPlass', 'Be om plass')}
                </Button>
              )}
              {trip.is_my_booking && trip.free_seats > 0 && (
                <Button variant="ghost" size="sm" className="h-6 text-[10px] px-2 text-blue-600" onClick={() => setSeatRequestModal({ open: true, trip })}>
                  + Flere
                </Button>
              )}
            </td>
          </>
        )}
      </tr>
      {canEdit && expandedRows.has(trip.trip_number) && (
        <tr className="bg-muted/50">
          <td colSpan={100} className="px-4 py-3">
            <BookingDetailsTable trip={trip} getLocationColor={getLocationColor} getCompanyShortName={getCompanyShortName} getLocationShortName={getLocationShortName} />
          </td>
        </tr>
      )}
    </React.Fragment>
  );
}

export { TripRow };

/** Iter 844: turens Type (T/MB/B, S=delt, P=per pax) — samme kode som Type-kolonnen i Booking-listen. Flere koder på turen vises «SB/P». */
export { tripTypeCode } from './tripType';

export function TripTypeCode({ trip }: { trip: any }) {
  const code = tripTypeCode(trip);
  if (!code) return <span className="text-muted-foreground">—</span>;
  return <span className="text-[10px] font-bold tracking-wide whitespace-nowrap" title={tripTypeTitle(code)} data-type-code={code}>{code}</span>;
}

/** Iter 842/843: «Bekreftet»-hake per tur. Grønn hake m/ kilde-tooltip; klikk = manuell overstyring fra kontoret.
 *  Ubekreftet tur i morgen får rød, pulserende ramme + varselikon så den er lett å få øye på. */
function TripConfirmedCheckbox({ trip, onToggle, tripDate }: { trip: any; onToggle: (trip: any, confirmed: boolean) => Promise<void> | void; tripDate?: string }) {
  const { t } = useTranslation();
  const [busy, setBusy] = React.useState(false);
  const checked = !!trip.confirmed;
  const isTomorrow = !!tripDate && tripDate.slice(0, 10) === format(addDays(new Date(), 1), 'yyyy-MM-dd');
  const urgent = isTomorrow && !checked;
  const when = trip.confirmed_at ? (() => { try { return format(parseISO(trip.confirmed_at), 'dd.MM HH:mm'); } catch { return ''; } })() : '';
  const title = !checked
    ? (urgent
      ? t('weeklyDateView.ubekreftetIMorgen', 'Turen går i morgen og er ikke bekreftet — purr sjåføren eller huk av manuelt')
      : t('weeklyDateView.ikkeBekreftet', 'Ikke bekreftet — klikk for å huke av manuelt'))
    : trip.confirmed_source === 'driver'
      ? t('weeklyDateView.bekreftetAvSjafor', { defaultValue: 'Bekreftet av sjåfør i portalen ({{name}} {{when}})', name: trip.confirmed_by || '', when })
      : t('weeklyDateView.bekreftetManuelt', { defaultValue: 'Huket av manuelt av {{name}} {{when}}', name: trip.confirmed_by || '', when });
  const click = async () => {
    if (busy) return;
    setBusy(true);
    try { await onToggle(trip, !checked); } finally { setBusy(false); }
  };
  return (
    <span className="inline-flex items-center gap-1">
      {urgent && <AlertTriangle className="w-3.5 h-3.5 text-red-600 animate-pulse" aria-hidden data-testid={`weekly-confirmed-urgent-${trip.trip_number}`} />}
      <button
        type="button"
        role="checkbox"
        aria-checked={checked}
        aria-label={t('weeklyDateView.bekreftet', 'Bekreftet')}
        title={title}
        disabled={busy}
        onClick={click}
        className={`inline-flex items-center justify-center w-5 h-5 rounded border-2 transition-colors ${checked ? 'bg-emerald-600 border-emerald-600 text-white' : urgent ? 'border-red-500 ring-2 ring-red-300 bg-red-50 dark:bg-red-900/30 hover:border-emerald-500' : 'border-slate-400 hover:border-emerald-500 bg-white dark:bg-slate-800'} ${busy ? 'opacity-50' : ''}`}
        data-testid={`weekly-confirmed-${trip.trip_number}`}
        data-source={trip.confirmed_source || ''}
        data-urgent={urgent ? 'true' : 'false'}
      >
        {checked && <Check className="w-3.5 h-3.5" strokeWidth={3} />}
        {checked && trip.confirmed_source === 'office' && <span className="sr-only">{t('weeklyDateView.manuell', 'manuell')}</span>}
      </button>
    </span>
  );
}

/** Iter 839: kunder på turen med pax per kunde — én linje per kunde, størst først (som «Kunde / Oppdrag» i bussplanen). */
export function customerPaxSummary(trip: any): Array<{ company: string; pax: number }> {
  const byCompany = new Map<string, number>();
  (trip?.bookings || []).forEach((b: any) => {
    const name = String(b?.company_name || '').trim() || '—';
    byCompany.set(name, (byCompany.get(name) || 0) + (Number(b?.number_of_passengers) || 0));
  });
  if (!byCompany.size) (trip?.companies || []).forEach((c: string) => byCompany.set(c, 0));
  return Array.from(byCompany.entries()).map(([company, pax]) => ({ company, pax })).sort((a, b) => b.pax - a.pax || a.company.localeCompare(b.company, 'nb'));
}

function CustomerPaxList({ trip, getCompanyShortName }: { trip: any; getCompanyShortName?: (name: string) => string }) {
  const rows = customerPaxSummary(trip);
  if (!rows.length) return <span className="text-muted-foreground">—</span>;
  return (
    <ul className="space-y-0.5 min-w-[120px] max-w-[220px]">
      {rows.map((r) => {
        const short = getCompanyShortName ? getCompanyShortName(r.company) : r.company;
        return (
          <li key={r.company} className="flex items-baseline gap-1 leading-tight" title={`${r.company}: ${r.pax} pax`}>
            <span className="truncate text-[10px]">{short}</span>
            <span className="text-[10px] font-semibold tabular-nums text-slate-700 dark:text-slate-200 shrink-0">({r.pax})</span>
          </li>
        );
      })}
    </ul>
  );
}
